import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  FormGroup,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
} from "reactstrap";
import { useCustomContext } from "../../../_helper/customizer";
import { HugeiconsIcon } from "@hugeicons/react";
import { Search01Icon } from "@hugeicons/core-free-icons";
import { Controller } from "react-hook-form";
import { Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useLocalizedText } from "../../../utils/hooks/useLocalizedText";
import { AntInput } from "../../../CommonElements/AntInput";

export const GroupsModal = ({ control, errors, setValue, watch, groups }) => {
  const [modal, setModal] = useState(false);
  const { layout } = useCustomContext();
  const [tempSelected, setTempSelected] = useState([]); // Temporary selection in modal
  const [savedSelected, setSavedSelected] = useState([]); // Actually saved selection
  const [searchTerm, setSearchTerm] = useState(""); // Search term state
  const selectAllRef = useRef(null);
  const { t } = useTranslation();

  const { getLocalizedText } = useLocalizedText();

  // Watch the form value to sync with saved state
  const watchedGroups = watch ? watch("group_ids") : [];

  useEffect(() => {
    // Ensure watchedGroups is always an array
    const groupsArray = Array.isArray(watchedGroups) ? watchedGroups : [];
    setSavedSelected(groupsArray);
  }, [watchedGroups]);

  const toggle = () => {
    if (modal) {
      // Modal is closing - revert to saved state
      const groupsToRevert = Array.isArray(savedSelected) ? savedSelected : [];
      setTempSelected(groupsToRevert);
      setSearchTerm(""); // Clear search when closing modal
    } else {
      // Modal is opening - initialize temp state with saved state
      const groupsToRevert = Array.isArray(savedSelected) ? savedSelected : [];
      setTempSelected(groupsToRevert);
    }
    setModal(!modal);
  };

  // Function to get proper Arabic plural form
  const getGroupText = (count) => {
    if (count === 0) {
      return t("SELECT_GROUPS");
    } else if (count === 1) {
      return t("ONE_GROUP_SELECTED");
    } else if (count === 2) {
      return t("TWO_GROUPS_SELECTED");
    } else {
      return t("MULTIPLE_GROUPS_SELECTED", { count });
    }
  };

  const title = ({ children }) => (
    <h5 className="w-100 d-flex align-items-center justify-content-center gap-2">
      {children}
    </h5>
  );
  const dir = layout === "rtl" ? "ltr" : "rtl";

  const data = [
    {
      id: 1,
      name: "شركة رواف منى",
      permissions: "عرض الصلاحيات",
      system: "نظام حجاج مكة",
    },
    {
      id: 2,
      name: "شركة الرواد",
      permissions: "عرض الصلاحيات",
      system: "نظام حجاج مكة",
    },
  ];

  // Filter groups based on search term
  const filteredGroups =
    groups?.filter((group) => {
      if (!searchTerm.trim()) return true;

      const localizedName = getLocalizedText(group.name_ar, group.name_en);
      return localizedName.toLowerCase()?.includes(searchTerm.toLowerCase());
    }) || [];

  // Handle individual checkbox change (only affects temporary state)
  const handleCheckboxChange = (checked, id) => {
    // Ensure tempSelected is always an array
    const currentTempSelected = Array.isArray(tempSelected) ? tempSelected : [];

    let newTempSelected;
    if (checked) {
      newTempSelected = [...currentTempSelected, id];
    } else {
      newTempSelected = currentTempSelected.filter((item) => item !== id);
    }
    setTempSelected(newTempSelected);
  };

  // Handle select all checkbox (only affects temporary state)
  const handleSelectAll = (checked) => {
    let newTempSelected;
    if (checked) {
      newTempSelected = filteredGroups.map((item) => item?.id);
    } else {
      newTempSelected = [];
    }
    setTempSelected(newTempSelected);
  };

  // Save the temporary selection to form and close modal
  const handleSave = () => {
    // Ensure tempSelected is always an array
    const groupsToSave = Array.isArray(tempSelected) ? tempSelected : [];
    setSavedSelected(groupsToSave);
    if (setValue) {
      setValue("group_ids", groupsToSave, {
        shouldValidate: true, // Trigger validation
        shouldDirty: true, // Mark field as dirty
        shouldTouch: true, // Mark field as touched
      });
    }
    toggle();
  };

  // Cancel and revert to saved state
  const handleCancel = () => {
    // Ensure savedSelected is always an array
    const groupsToRevert = Array.isArray(savedSelected) ? savedSelected : [];
    setTempSelected(groupsToRevert);
    setSearchTerm(""); // Clear search when canceling
    toggle();
  };

  // Calculate checkbox states based on temporary selection and filtered groups
  const allSelected =
    Array.isArray(tempSelected) &&
    tempSelected?.length === filteredGroups?.length &&
    filteredGroups?.length > 0;
  const someSelected =
    Array.isArray(tempSelected) &&
    tempSelected?.length > 0 &&
    tempSelected?.length < filteredGroups?.length;
  const indeterminate = someSelected;

  // Set indeterminate state using useEffect
  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <>
      <FormGroup>
        <div>
          <label className="fw-bold" htmlFor="group_ids">
            {t("USER_GROUP")}
          </label>
        </div>
        <Controller
          name="group_ids"
          control={control}
          render={({ field }) => (
            <>
              <Button
                type="button"
                id="group_ids"
                outline
                className={
                  errors?.group_ids
                    ? "border-danger justify-content-start"
                    : "justify-content-start"
                }
                onClick={toggle}
              >
                {getGroupText(
                  Array.isArray(savedSelected) ? savedSelected?.length : 0
                )}
              </Button>
              <Input
                name="group_ids"
                {...field}
                value={savedSelected}
                className="d-none"
                invalid={!!errors?.group_ids}
              />
            </>
          )}
        />
        {errors?.group_ids && (
          <div className="text-danger mt-1">{errors.group_ids.message}</div>
        )}
      </FormGroup>

      <Modal size="lg" isOpen={modal} toggle={handleCancel} centered>
        <ModalHeader dir={dir} toggle={handleCancel} tag={title}>
          <span className="fw-bold H5"> {t("CHOOSE_USER_GROUPS")}</span>
        </ModalHeader>
        <ModalBody>
          {filteredGroups?.length > 0 ? (
            <>
              <AntInput
                type="text"
                placeholder={t("SEARCH_GROUP")}
                className="mb-3"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <Table>
                <thead>
                  <tr>
                    <th>
                      <Input
                        ref={selectAllRef}
                        type="checkbox"
                        checked={allSelected}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    </th>
                    <th>{t("GROUP_NAME_COLUMN")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredGroups?.map((group) => (
                    <tr key={group?.id}>
                      <td>
                        <Input
                          prefix={<HugeiconsIcon icon={Search01Icon} />}
                          onChange={(e) =>
                            handleCheckboxChange(e.target.checked, group?.id)
                          }
                          type="checkbox"
                          checked={
                            Array.isArray(tempSelected) &&
                            tempSelected?.includes(group?.id)
                          }
                        />
                      </td>
                      <td>{getLocalizedText(group.name_ar, group.name_en)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          ) : (
            <div className="text-center py-5">
              <div className="mb-3">
                <HugeiconsIcon
                  icon={Search01Icon}
                  size={48}
                  className="text-muted"
                />
              </div>
              <h5 className="text-muted mb-2">
                {searchTerm.trim() ? (
                  <span className="fw-bold H5">t("NO_GROUPS_FOUND")</span>
                ) : (
                  <span className="fw-bold H5">{t("NO_GROUPS")}</span>
                )}
              </h5>
              <p className="text-muted mb-0">
                {searchTerm.trim()
                  ? t("NO_GROUPS_DESCRIPTION")
                  : t("NO_GROUPS_DESCRIPTION")}
              </p>
            </div>
          )}
        </ModalBody>
        {filteredGroups?.length > 0 && (
          <ModalFooter dir={dir}>
            <Stack direction="horizontal" gap={2}>
              <Button color="danger" size="xs" onClick={handleCancel}>
                {t("CANCEL")}
              </Button>
              <Button color="primary" size="xs" onClick={handleSave}>
                {t("SAVE")}
              </Button>
            </Stack>
          </ModalFooter>
        )}
      </Modal>
    </>
  );
};
